import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import type { DueCounts, Rating, ReviewCard } from '../../sdk/@types/Revisao';
import ReviewService, { type GetDueParams } from '../../sdk/services/ReviewService';
import { cartoesDoDia, cartoesPorIds } from '../../app/components/Revisao/conclusaoLeitura';
import {
  API_OFERECE_DESFAZER,
  CONTAGENS_VAZIAS,
  SESSAO_INICIAL,
  aplicarDesfazer,
  cancelarRegistro,
  cartaoCorrente,
  concluirRegistro,
  faseDaSessao,
  filaCarregada,
  filaExata,
  filaRejeitada,
  iniciarRegistro,
  podeDesfazer,
  podeRecuar,
  progresso,
  pularCartao,
  recuarCartao,
  substituirCartao,
  type RevisaoSessaoVista,
  type SessaoEstado,
} from './revisaoSessao';

export interface FiltroSessao extends GetDueParams {
  /** When non-empty, the queue is exactly these cards from GET /review/cards, in this order. getDue is not called. */
  cardIds?: string[];
  /** When set (and cardIds is empty), GET /review/due runs with the other fields, then only cards whose due day is this yyyy-MM-dd stay. */
  somenteDia?: string;
}

/** Contagens dos cartões que ficaram na fila. Status fora de new/learning/review não entra. */
function contagensDosCartoes(cards: readonly ReviewCard[]): DueCounts {
  const contagens: DueCounts = { new: 0, learning: 0, review: 0 };
  for (const card of cards) {
    if (card.status === 'new' || card.status === 'learning' || card.status === 'review') {
      contagens[card.status] += 1;
    }
  }
  return contagens;
}

/** Os campos novos não vão na query de getDue. */
function parametrosGetDue(filtro: FiltroSessao): GetDueParams {
  return {
    limit: filtro.limit,
    subjectId: filtro.subjectId,
    subjectIds: filtro.subjectIds,
    tag: filtro.tag,
    tags: filtro.tags,
    ahead: filtro.ahead,
    dueBefore: filtro.dueBefore,
  };
}

/**
 * Sessão de estudo em memória. A fase, a deduplicação, o pular, o recuar
 * e a trava de um registro em curso vêm de `revisaoSessao`. Nada daqui é
 * persistido (design, Decisão 3).
 */
export default function useRevisaoSessao(filtro: FiltroSessao): RevisaoSessaoVista {
  const [estado, setEstado] = useState<SessaoEstado>(SESSAO_INICIAL);
  const [contagens, setContagens] = useState(CONTAGENS_VAZIAS);
  const [iniciadaEm, setIniciadaEm] = useState<number | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const [cartaoMostradoEm, setCartaoMostradoEm] = useState<number | null>(null);

  // Fonte da trava. Atualizado na hora da transição, não no render: um
  // segundo clique no mesmo turno lê `registrando` antes do React pintar.
  const estadoRef = useRef<SessaoEstado>(SESSAO_INICIAL);
  const mostradoRef = useRef<number | null>(null);
  const desfazendoRef = useRef(false);
  /** Sobe a cada leitura. Nota ou desfazer de uma leitura anterior não entra. */
  const geracaoRef = useRef(0);
  const filtroRef = useRef(filtro);
  filtroRef.current = filtro;

  // Uma passagem só. A transição lê o espelho, não o estado do último render,
  // senão dois cliques no mesmo turno aplicariam os dois sobre a fila antiga.
  const aplicar = useCallback((transicao: (atual: SessaoEstado) => SessaoEstado) => {
    const next = transicao(estadoRef.current);
    estadoRef.current = next;
    setEstado(next);
  }, []);

  const subjectId = filtro.subjectId;
  const subjectIdsKey = filtro.subjectIds?.join(',');
  const tag = filtro.tag;
  const tagsKey = filtro.tags?.join(',');
  const limit = filtro.limit;
  const ahead = filtro.ahead;
  const dueBefore = filtro.dueBefore;
  const cardIdsKey = filtro.cardIds?.join(',');
  const somenteDia = filtro.somenteDia;

  useEffect(() => {
    let ativo = true;
    geracaoRef.current += 1;
    desfazendoRef.current = false;
    aplicar(() => SESSAO_INICIAL);
    setContagens(CONTAGENS_VAZIAS);
    setIniciadaEm(null);

    const pedido = filtroRef.current;
    const cardIds = pedido.cardIds ?? [];
    const dia = pedido.somenteDia;

    const rejeitar = () => {
      if (!ativo) return;
      // Rejeição não é fila vazia: a fase sai `falhou`, sem toast.
      aplicar(() => filaRejeitada());
    };

    if (cardIds.length > 0) {
      const ids = cardIds.slice();
      ReviewService.listCards().then((cards) => {
        if (!ativo) return;
        if (!Array.isArray(cards)) {
          aplicar(() => filaRejeitada());
          return;
        }
        const fila = cartoesPorIds(cards, ids);
        aplicar(() => filaExata(fila));
        setContagens(contagensDosCartoes(fila));
        setIniciadaEm(Date.now());
      }, rejeitar);
    } else if (dia) {
      ReviewService.getDue(parametrosGetDue(pedido)).then((res) => {
        if (!ativo) return;
        const doDia = cartoesDoDia(res?.queue ?? [], dia);
        // A previsão conta cada linha de agendamento. Uma oclusão com o mesmo
        // templateId são cartões diferentes — dedupeQueue derrubaria parte dos 42.
        aplicar(() => filaExata(doDia));
        setContagens(contagensDosCartoes(doDia));
        setIniciadaEm(Date.now());
      }, rejeitar);
    } else {
      ReviewService.getDue(parametrosGetDue(pedido)).then((res) => {
        if (!ativo) return;
        aplicar(() => filaCarregada(res?.queue));
        setContagens(res?.counts ?? CONTAGENS_VAZIAS);
        setIniciadaEm(Date.now());
      }, rejeitar);
    }

    return () => {
      ativo = false;
    };
  }, [subjectId, subjectIdsKey, tag, tagsKey, limit, ahead, dueBefore, cardIdsKey, somenteDia, tentativa, aplicar]);

  const registrar = useCallback(
    (rating: Rating) => {
      const atual = estadoRef.current;
      const card = cartaoCorrente(atual);
      if (!card) return;
      const locked = iniciarRegistro(atual);
      if (!locked) return;
      const geracao = geracaoRef.current;
      aplicar(() => locked);
      const elapsedMs = Math.max(0, Date.now() - (mostradoRef.current ?? Date.now()));
      const em = Date.now();
      ReviewService.answer(card.id, rating, elapsedMs).then(
        () => {
          if (geracaoRef.current !== geracao) return;
          aplicar((s) => concluirRegistro(s, rating, elapsedMs, em));
        },
        () => {
          if (geracaoRef.current !== geracao) return;
          aplicar((s) => cancelarRegistro(s));
          notification.error({ message: 'Não foi possível registrar a revisão.' });
        }
      );
    },
    [aplicar]
  );

  const pular = useCallback(() => {
    aplicar(pularCartao);
  }, [aplicar]);

  const desfazer = useCallback(() => {
    const atual = estadoRef.current;
    if (!podeDesfazer(atual.notas, API_OFERECE_DESFAZER, atual.registrando)) return;
    if (desfazendoRef.current) return;
    const ultima = atual.notas[atual.notas.length - 1];
    if (!ultima) return;
    const geracao = geracaoRef.current;
    desfazendoRef.current = true;
    ReviewService.undo(ultima.cardId).then(
      (card) => {
        if (geracaoRef.current !== geracao) return;
        desfazendoRef.current = false;
        aplicar((s) => aplicarDesfazer(s, card, API_OFERECE_DESFAZER));
      },
      () => {
        if (geracaoRef.current !== geracao) return;
        desfazendoRef.current = false;
      }
    );
  }, [aplicar]);

  const recuar = useCallback(() => {
    aplicar(recuarCartao);
  }, [aplicar]);

  const atualizarCartao = useCallback(
    (cartao: ReviewCard) => {
      aplicar((s) => substituirCartao(s, cartao));
    },
    [aplicar],
  );

  const tentarDeNovo = useCallback(() => {
    setTentativa((n) => n + 1);
  }, []);

  const fase = faseDaSessao(estado);

  useEffect(() => {
    if (fase !== 'estudando') {
      mostradoRef.current = null;
      setCartaoMostradoEm(null);
      return;
    }
    const agora = Date.now();
    mostradoRef.current = agora;
    setCartaoMostradoEm(agora);
  }, [fase, estado.indice]);

  const { posicao, total, faltam } = progresso(estado);

  return {
    fase,
    fila: estado.fila,
    indice: estado.indice,
    cartao: cartaoCorrente(estado),
    notas: estado.notas,
    contagens,
    posicao,
    total,
    faltam,
    registrando: estado.registrando,
    podeDesfazer: podeDesfazer(estado.notas, API_OFERECE_DESFAZER, estado.registrando),
    podeRecuar: podeRecuar(estado),
    registrar,
    pular,
    desfazer,
    recuar,
    atualizarCartao,
    tentarDeNovo,
    iniciadaEm,
    cartaoMostradoEm,
  };
}
